import { useCallback, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadSimple, Trash } from '@phosphor-icons/react';
import { CompanyLogoType } from '@monorepo/shared';

const MAX_LOGO_SIZE = 10 * 1024 * 1024;
const ALLOWED_LOGO_MIME_TYPES = ['image/jpeg', 'image/png'];

export interface PendingCompanyLogoSectionRef {
  getFiles: () => { full?: File; square?: File };
}

interface PendingLogoUploadProps {
  type: CompanyLogoType;
  onFileChange: (type: CompanyLogoType, file: File | null) => void;
  file: File | null;
}

const PendingLogoUpload = ({ type, onFileChange, file }: PendingLogoUploadProps) => {
  const { t } = useTranslation();
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateAndSelect = useCallback(
    (selectedFile: File) => {
      setFileError(null);

      if (!ALLOWED_LOGO_MIME_TYPES.includes(selectedFile.type)) {
        setFileError(t('company.logo.upload_error.invalid_format'));
        return;
      }

      if (selectedFile.size > MAX_LOGO_SIZE) {
        setFileError(t('company.logo.upload_error.file_too_large'));
        return;
      }

      onFileChange(type, selectedFile);
    },
    [onFileChange, type, t],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) {
        validateAndSelect(droppedFile);
      }
    },
    [validateAndSelect],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (selectedFile) {
        validateAndSelect(selectedFile);
      }
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [validateAndSelect],
  );

  const labelKey =
    type === CompanyLogoType.FULL ? 'company.logo.full_label' : 'company.logo.square_label';

  if (file) {
    const previewUrl = URL.createObjectURL(file);

    return (
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-gray-700">{t(labelKey)}</span>
        <div className="flex items-center gap-4 p-3 border border-gray-200 rounded-md bg-gray-50">
          <img
            src={previewUrl}
            alt={t(labelKey)}
            className="h-16 object-contain rounded"
            onLoad={() => URL.revokeObjectURL(previewUrl)}
          />
          <button
            type="button"
            onClick={() => onFileChange(type, null)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition"
          >
            <Trash size={16} />
            {t('company.logo.delete_button')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-gray-700">{t(labelKey)}</span>
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          flex flex-col items-center justify-center gap-3 p-6
          border border-dashed rounded-md transition-colors
          ${isDragOver ? 'border-primary bg-primary/5' : 'border-gray-300 bg-white'}
        `}
      >
        <UploadSimple size={36} className="text-gray-400" />
        <p className="text-xs text-gray-500 text-center">{t('company.logo.upload_hint')}</p>

        {fileError && <p className="text-xs text-red-600">{fileError}</p>}

        <label className="cursor-pointer">
          <input
            ref={inputRef}
            type="file"
            accept=".jpg,.jpeg,.png,image/jpeg,image/png"
            onChange={handleInputChange}
            className="hidden"
          />
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-900 text-sm font-medium rounded-md hover:bg-gray-200 transition shadow-[0px_2px_10px_0px_rgba(0,41,102,0.08)]">
            {t('company.logo.import_button')}
          </span>
        </label>
      </div>
    </div>
  );
};

export const PendingCompanyLogoSection = forwardRef<PendingCompanyLogoSectionRef>((_, ref) => {
  const [fullFile, setFullFile] = useState<File | null>(null);
  const [squareFile, setSquareFile] = useState<File | null>(null);

  useImperativeHandle(ref, () => ({
    getFiles: () => ({
      ...(fullFile ? { full: fullFile } : {}),
      ...(squareFile ? { square: squareFile } : {}),
    }),
  }));

  const handleFileChange = useCallback((type: CompanyLogoType, file: File | null) => {
    if (type === CompanyLogoType.FULL) {
      setFullFile(file);
    } else {
      setSquareFile(file);
    }
  }, []);

  return (
    <div className="flex flex-col gap-6">
      <PendingLogoUpload
        type={CompanyLogoType.FULL}
        onFileChange={handleFileChange}
        file={fullFile}
      />
      <PendingLogoUpload
        type={CompanyLogoType.SQUARE}
        onFileChange={handleFileChange}
        file={squareFile}
      />
    </div>
  );
});

PendingCompanyLogoSection.displayName = 'PendingCompanyLogoSection';
